import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Users, Shield, FileDown, RotateCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import logger from "@/lib/logger";
import MasterDataTab from "./settings/MasterDataTab";
import ShiftConfigTab from "./settings/ShiftConfigTab";
import AlertConfigTab from "./settings/AlertConfigTab";
import StaleDraftsTab from "./settings/StaleDraftsTab";
import ReportConfigTab from "./settings/ReportConfigTab";
import UserManagementTab from "./settings/UserManagementTab";
import EmailLogsTab from "./settings/EmailLogsTab";
import AuditTrailTab from "./settings/AuditTrailTab";
import DataResetTab from "./settings/DataResetTab";

export default function Settings() {
  const [tab, setTab] = useState("masters");
  const { user } = useAuth();

  // Managers get access only to the Audit Trail tab; everyone else (guard/user) is blocked.
  const isManagerOnly = user?.role === "manager";
  useEffect(() => {
    if (isManagerOnly && tab !== "auditlog") setTab("auditlog");
  }, [isManagerOnly, tab]);

  if (user?.role !== "admin" && user?.role !== "manager") {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Chivo" }}>Settings</h1>
          <p className="text-sm text-gray-500">{isManagerOnly ? "Manager view — audit log & oversight" : "Admin configuration & master data management"}</p>
        </div>
        <Button
          variant="outline"
          className="h-9 text-xs gap-1.5 border-[#1B3C87] text-[#1B3C87] hover:bg-[#1B3C87]/5"
          data-testid="download-prd-btn"
          onClick={async () => {
            try {
              const res = await api.get("/manual/download/prd", { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement("a"); a.href = url; a.download = "GMS_PRD_v1.0.md"; a.click();
              window.URL.revokeObjectURL(url);
              toast.success("PRD downloaded");
            } catch (err) { logger.error("Download failed:", err); toast.error("Download failed"); }
          }}
        >
          <FileDown size={14} /> Download PRD
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-10 sm:h-11 bg-[#1B3C87]/5 flex-wrap">
          {!isManagerOnly && (
            <TabsTrigger value="masters" className="h-8 sm:h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid="tab-masters">
              Master Data
            </TabsTrigger>
          )}
          {!isManagerOnly && (
            <TabsTrigger value="shifts" className="h-8 sm:h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid="tab-shifts">
              Shift Config
            </TabsTrigger>
          )}
          {!isManagerOnly && (
            <TabsTrigger value="alerts" className="h-8 sm:h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid="tab-alerts">
              Alert Config
            </TabsTrigger>
          )}
          {!isManagerOnly && (
            <TabsTrigger value="stale" className="h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="tab-stale">
              Stale Drafts
            </TabsTrigger>
          )}
          {!isManagerOnly && (
            <TabsTrigger value="reportconfig" className="h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid="tab-reportconfig">
              <Mail size={14} className="mr-1 hidden sm:block" /> Report Config
            </TabsTrigger>
          )}
          {!isManagerOnly && (
            <TabsTrigger value="users" className="h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid="tab-users">
              <Users size={14} className="mr-1 hidden sm:block" /> User Management
            </TabsTrigger>
          )}
          {user?.role === "admin" && (
            <TabsTrigger value="emaillogs" className="h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-emaillogs">
              <Mail size={14} className="mr-1 hidden sm:block" /> Email Logs
            </TabsTrigger>
          )}
          {(user?.role === "admin" || user?.role === "manager") && (
            <TabsTrigger value="auditlog" className="h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-slate-700 data-[state=active]:text-white" data-testid="tab-auditlog">
              <Shield size={14} className="mr-1 hidden sm:block" /> Audit Trail
            </TabsTrigger>
          )}
          {user?.role === "admin" && (
            <TabsTrigger value="datareset" className="h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-red-600 data-[state=active]:text-white" data-testid="tab-datareset">
              <RotateCw size={14} className="mr-1 hidden sm:block" /> FY Reset
            </TabsTrigger>
          )}
        </TabsList>

        {!isManagerOnly && <TabsContent value="masters"><MasterDataTab /></TabsContent>}
        {!isManagerOnly && <TabsContent value="shifts"><ShiftConfigTab /></TabsContent>}
        {!isManagerOnly && <TabsContent value="alerts"><AlertConfigTab /></TabsContent>}
        {!isManagerOnly && <TabsContent value="stale"><StaleDraftsTab /></TabsContent>}
        {!isManagerOnly && <TabsContent value="reportconfig"><ReportConfigTab /></TabsContent>}
        {!isManagerOnly && <TabsContent value="users"><UserManagementTab /></TabsContent>}
        {user?.role === "admin" && <TabsContent value="emaillogs"><EmailLogsTab /></TabsContent>}
        <TabsContent value="auditlog"><AuditTrailTab /></TabsContent>
        {user?.role === "admin" && <TabsContent value="datareset"><DataResetTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
